import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { usePortalAuth, portalApi } from "../../context/PortalAuthContext";
import { ArrowLeft, Upload, CheckCircle, Clock, Copy, ExternalLink, MapPin, Truck } from "lucide-react";
import { toast } from "sonner";
import { formatMXN } from "../../utils/format";

const BANK_DATA = {
  bank: "Banregio",
  owner: "Christian Carmona Guasque",
  clabe: "058597000008643380",
};

function CountdownTimer({ expiresAt, onExpired }) {
  const [timeLeft, setTimeLeft] = useState(null);

  useEffect(() => {
    const tick = () => {
      const diff = new Date(expiresAt) - new Date();
      if (diff <= 0) { setTimeLeft(null); onExpired?.(); return; }
      const h = Math.floor(diff / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      setTimeLeft({ h, m, s, urgent: diff < 3600000 });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [expiresAt, onExpired]);

  if (!timeLeft) return (
    <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-center text-sm text-red-700 font-medium">
      ⚠️ El tiempo de reserva venció. Otros clientes podrían tomar tu lugar.
    </div>
  );

  return (
    <div className={`rounded-xl p-4 text-center ${timeLeft.urgent ? "bg-red-50 border border-red-200" : "bg-amber-50 border border-amber-200"}`}>
      <div className="flex items-center justify-center gap-1.5 mb-1">
        <Clock size={14} className={timeLeft.urgent ? "text-red-600" : "text-amber-600"} />
        <span className={`text-xs font-semibold ${timeLeft.urgent ? "text-red-700" : "text-amber-700"}`}>
          {timeLeft.urgent ? "¡Paga ahora o pierdes tu lugar!" : "Tu lugar en la fila está reservado por:"}
        </span>
      </div>
      <div className={`text-3xl font-bold tracking-widest ${timeLeft.urgent ? "text-red-700" : "text-amber-700"}`}>
        {String(timeLeft.h).padStart(2, "0")}:{String(timeLeft.m).padStart(2, "0")}:{String(timeLeft.s).padStart(2, "0")}
      </div>
      <p className="text-xs mt-1 text-slate-500">Una vez que pagues, aseguras tu posición</p>
    </div>
  );
}

function CopyField({ label, value }) {
  const copy = () => { navigator.clipboard.writeText(value); toast.success(`${label} copiado`); };
  return (
    <div className="flex items-center justify-between py-2.5 border-b border-slate-100 last:border-0">
      <div>
        <div className="text-xs text-slate-400 font-medium">{label}</div>
        <div className="text-sm font-semibold text-slate-900 mt-0.5">{value}</div>
      </div>
      <button onClick={copy} className="p-2 hover:bg-slate-100 rounded-lg transition-colors text-slate-400 hover:text-brand-600">
        <Copy size={14} />
      </button>
    </div>
  );
}

export default function PortalOrderDetail() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { logout } = usePortalAuth();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [file, setFile] = useState(null);
  const fileRef = useRef();

  const load = async () => {
    try {
      const res = await portalApi.get(`/orders/${orderId}`);
      setOrder(res.data);
    } catch (err) {
      if (err.response?.status === 401) { logout(); navigate("/tienda/login"); }
      else toast.error("No se pudo cargar el pedido");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [orderId]);

  const handleUpload = async () => {
    if (!file) { toast.error("Selecciona un comprobante"); return; }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      await portalApi.post(`/orders/${orderId}/payment-proof`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("¡Comprobante enviado! Te notificaremos cuando sea validado.");
      setFile(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al subir comprobante");
    } finally {
      setUploading(false);
    }
  };

  const handleMercadoPago = () => {
    if (order?.mp_init_point) {
      window.open(order.mp_init_point, "_blank");
    } else {
      toast.info("MercadoPago se configurará próximamente. Usa transferencia bancaria por ahora.");
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-400">Cargando...</div>;
  if (!order) return <div className="min-h-screen flex items-center justify-center text-slate-400">Pedido no encontrado</div>;

  const isPendingPayment = order.portal_status === "pendiente_pago";
  const proofSent = ["pago_enviado", "pago_validado", "en_produccion", "enviado"].includes(order.portal_status);

  const statusSteps = [
    { key: "pendiente_pago", label: "Pedido creado" },
    { key: "pago_enviado", label: "Pago enviado" },
    { key: "pago_validado", label: "Pago validado" },
    { key: "en_produccion", label: "En preparación" },
    { key: "enviado", label: "Enviado" },
  ];
  const currentStep = statusSteps.findIndex(s => s.key === order.portal_status);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-brand-600 text-white sticky top-0 z-40">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-3">
          <button onClick={() => navigate("/tienda")} className="p-1.5 hover:bg-white/10 rounded-lg" data-testid="back-btn">
            <ArrowLeft size={18} />
          </button>
          <div className="flex-1">
            <div className="font-bold text-sm">{order.folio}</div>
            <div className="text-brand-200 text-xs">{formatMXN(order.total)}</div>
          </div>
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-4 py-4 space-y-4">
        {/* Progress steps */}
        <div className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm overflow-x-auto">
          <div className="flex items-center justify-between min-w-[400px]">
            {statusSteps.map((step, i) => (
              <React.Fragment key={step.key}>
                <div className="flex flex-col items-center">
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${
                    i <= currentStep ? "bg-brand-600 text-white" : i === currentStep + 1 ? "bg-brand-100 text-brand-400 border-2 border-brand-200" : "bg-slate-100 text-slate-400"
                  }`}>
                    {i < currentStep ? <CheckCircle size={14} /> : i + 1}
                  </div>
                  <div className={`text-xs mt-1 text-center w-14 leading-tight ${i <= currentStep ? "text-brand-700 font-medium" : "text-slate-400"}`}>
                    {step.label}
                  </div>
                </div>
                {i < statusSteps.length - 1 && (
                  <div className={`flex-1 h-0.5 mx-1 -mt-4 ${i < currentStep ? "bg-brand-600" : "bg-slate-200"}`} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Countdown */}
        {isPendingPayment && order.payment_deadline && (
          <CountdownTimer expiresAt={order.payment_deadline} />
        )}

        {/* Shipping info */}
        {(order.shipping_state || order.shipping_address) && (
          <div className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm">
            <h3 className="font-semibold text-slate-900 mb-3 text-sm flex items-center gap-2">
              <Truck size={16} className="text-brand-600" /> Datos de envío
            </h3>
            <div className="space-y-2 text-sm">
              {order.shipping_state && (
                <div className="flex items-start gap-2">
                  <MapPin size={14} className="text-slate-400 mt-0.5" />
                  <div>
                    <span className="text-slate-700">{order.shipping_state}</span>
                    {order.shipping_postal_code && <span className="text-slate-400"> · CP {order.shipping_postal_code}</span>}
                  </div>
                </div>
              )}
              {order.shipping_address && (
                <div className="text-slate-600 pl-6">{order.shipping_address}</div>
              )}
            </div>
          </div>
        )}

        {/* Items */}
        <div className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm">
          <h3 className="font-semibold text-slate-900 mb-3 text-sm">Productos del pedido</h3>
          <div className="space-y-2">
            {order.items?.map((item, i) => (
              <div key={i} className="flex justify-between items-center text-sm">
                <div className="text-slate-700">{item.product_name} <span className="text-slate-400">×{item.quantity} {item.unit}</span></div>
                <div className="font-semibold text-slate-900">{formatMXN(item.subtotal)}</div>
              </div>
            ))}
            <div className="border-t border-slate-100 pt-2 space-y-1">
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Subtotal</span>
                <span>{formatMXN(order.subtotal || (order.total - (order.shipping_cost || 0)))}</span>
              </div>
              {(order.shipping_cost > 0 || order.shipping_state) && (
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Envío</span>
                  <span>{order.shipping_cost > 0 ? formatMXN(order.shipping_cost) : "GRATIS"}</span>
                </div>
              )}
              <div className="flex justify-between font-bold text-brand-700 pt-1">
                <span>Total</span><span>{formatMXN(order.total)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Payment section */}
        {(isPendingPayment || proofSent) && (
          <div className="space-y-3">
            {/* MercadoPago */}
            {order.payment_method === "mercadopago" && (
              <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-2xl">💳</span>
                  <div>
                    <div className="font-semibold text-blue-800 text-sm">Pago con MercadoPago</div>
                    <div className="text-xs text-blue-600">Acepta tarjetas de crédito, débito y más</div>
                  </div>
                </div>
                <button onClick={handleMercadoPago} data-testid="mercadopago-btn"
                  className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white py-2.5 rounded-xl text-sm font-semibold transition-colors">
                  <ExternalLink size={15} /> Ir a pagar con MercadoPago
                </button>
              </div>
            )}

            {/* Transferencia */}
            {order.payment_method === "transferencia" && (
              <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-2xl">🏦</span>
                  <div>
                    <div className="font-semibold text-slate-900 text-sm">Datos para transferencia</div>
                    <div className="text-xs text-slate-500">Referencia = tu nombre o empresa</div>
                  </div>
                </div>
                <div className="bg-slate-50 rounded-xl p-3">
                  <CopyField label="Banco" value={BANK_DATA.bank} />
                  <CopyField label="Titular" value={BANK_DATA.owner} />
                  <CopyField label="CLABE Interbancaria" value={BANK_DATA.clabe} />
                  <CopyField label="Monto exacto" value={`$${order.total.toFixed(2)} MXN`} />
                  <CopyField label="Referencia" value={order.customer_name || "Tu nombre"} />
                </div>
              </div>
            )}

            {/* Upload proof */}
            {!proofSent && (
              <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm">
                <h3 className="font-semibold text-slate-900 text-sm mb-3">📎 Subir comprobante de pago</h3>
                <div
                  onClick={() => fileRef.current?.click()}
                  data-testid="upload-area"
                  className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors ${file ? "border-brand-300 bg-brand-50" : "border-slate-200 hover:border-brand-300"}`}>
                  <input ref={fileRef} type="file" accept="image/*,.pdf" className="hidden" onChange={e => setFile(e.target.files[0])} />
                  {file ? (
                    <div className="text-sm text-brand-700 font-medium">{file.name}</div>
                  ) : (
                    <>
                      <Upload size={24} className="mx-auto text-slate-300 mb-2" />
                      <p className="text-sm text-slate-500">Toca para seleccionar imagen o PDF</p>
                    </>
                  )}
                </div>
                <button onClick={handleUpload} disabled={!file || uploading} data-testid="upload-proof-btn"
                  className="mt-3 w-full flex items-center justify-center gap-2 bg-brand-600 hover:bg-brand-700 text-white py-2.5 rounded-xl text-sm font-semibold transition-colors disabled:opacity-50">
                  {uploading ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Upload size={15} />}
                  Enviar comprobante
                </button>
              </div>
            )}

            {/* Proof sent confirmation */}
            {proofSent && (
              <div className={`rounded-2xl p-4 flex items-start gap-3 ${order.portal_status === "pago_validado" || order.portal_status === "en_produccion" || order.portal_status === "enviado" ? "bg-emerald-50 border border-emerald-200" : "bg-blue-50 border border-blue-200"}`}>
                {order.portal_status === "pago_enviado" ? (
                  <>
                    <Clock size={18} className="text-blue-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="font-semibold text-blue-800 text-sm">Comprobante en revisión</div>
                      <div className="text-xs text-blue-600 mt-0.5">Tu pago está siendo validado. Te notificaremos pronto.</div>
                    </div>
                  </>
                ) : (
                  <>
                    <CheckCircle size={18} className="text-emerald-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="font-semibold text-emerald-800 text-sm">¡Pago confirmado!</div>
                      <div className="text-xs text-emerald-600 mt-0.5">
                        {order.queue_position ? `Tu posición en la fila: #${order.queue_position + 4}` : "Tu pedido está en preparación."}
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
